'use client'

import apiClient from './axios';
import { useAuthStore } from '@/stores/auth-store';

export interface User {
  id: number;
  email: string;
  username: string;
  first_name: string;
  last_name: string;
  phone_number: string | null;
  is_verified: boolean;
  is_active: boolean;
  date_joined: string;
  profile?: UserProfile;
}

export interface UserProfile {
  id: number;
  avatar?: string;
  bio?: string;
  date_of_birth?: string;
  address?: string;
  city?: string;
  state?: string;
  country?: string;
  postal_code?: string;
  created_at: string;
  updated_at: string;
}

export interface AuthTokens {
  access: string;
  refresh: string;
}

export interface Tenant {
  id: string;
  name: string;
  slug: string;
  plan_type: string;
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface RegisterRequest {
  email: string;
  username: string;
  password: string;
  password_confirm: string;
  first_name: string;
  last_name: string;
  phone_number?: string;
}

export interface ChangePasswordRequest {
  old_password: string;
  new_password: string;
  new_password_confirm: string;
}

export interface PasswordResetRequest {
  email: string;
}

export interface PasswordResetConfirmRequest {
  token: string;
  new_password: string;
  new_password_confirm: string;
}

export interface EmailVerificationRequest {
  token: string;
}

// Error handling utility
function extractErrorMessage(error: any): string {
  if (typeof error === 'string') {
    return error;
  }
  
  // Handle the specific error format from backend: {"error": ["message"]}
  if (error?.error) {
    if (Array.isArray(error.error)) {
      return error.error[0];
    }
    return error.error;
  }
  
  if (error?.message) {
    return error.message;
  }
  
  if (error?.detail) {
    return error.detail;
  }
  
  // Handle Django REST Framework validation errors
  if (error?.email) {
    return Array.isArray(error.email) ? error.email[0] : error.email;
  }
  
  if (error?.password) {
    return Array.isArray(error.password) ? error.password[0] : error.password;
  }
  
  if (error?.password_confirm) {
    return Array.isArray(error.password_confirm) ? error.password_confirm[0] : error.password_confirm;
  }
  
  if (error?.username) {
    return Array.isArray(error.username) ? error.username[0] : error.username;
  }
  
  if (error?.first_name) {
    return Array.isArray(error.first_name) ? error.first_name[0] : error.first_name;
  }
  
  if (error?.last_name) {
    return Array.isArray(error.last_name) ? error.last_name[0] : error.last_name;
  }
  
  if (error?.phone_number) {
    return Array.isArray(error.phone_number) ? error.phone_number[0] : error.phone_number;
  }
  
  if (error?.old_password) {
    return Array.isArray(error.old_password) ? error.old_password[0] : error.old_password;
  }
  
  if (error?.new_password) {
    return Array.isArray(error.new_password) ? error.new_password[0] : error.new_password;
  }
  
  if (error?.new_password_confirm) {
    return Array.isArray(error.new_password_confirm) ? error.new_password_confirm[0] : error.new_password_confirm;
  }
  
  if (error?.token) {
    return Array.isArray(error.token) ? error.token[0] : error.token;
  }
  
  // Handle non-field errors
  if (error?.non_field_errors) {
    return Array.isArray(error.non_field_errors) ? error.non_field_errors[0] : error.non_field_errors;
  }
  
  // Handle general error object
  if (typeof error === 'object' && error !== null) {
    const firstKey = Object.keys(error)[0];
    if (firstKey) {
      const firstValue = error[firstKey];
      return Array.isArray(firstValue) ? firstValue[0] : firstValue;
    }
  }
  
  return 'An unexpected error occurred. Please try again.';
}

class AuthApiService {
  async login(data: LoginRequest): Promise<{ message: string; user: User; tokens: AuthTokens }> {
    try {
      const response = await apiClient.post('/api/auth/login/', data);
      
      // Store tokens in Zustand
      if (response.data.tokens?.access || response.data.tokens?.refresh) {
        useAuthStore.getState().setTokens(response.data.tokens.access, response.data.tokens.refresh);
      }
      
      return response.data;
    } catch (error: any) {
      throw new Error(extractErrorMessage(error.response?.data || error));
    }
  }

  async register(data: RegisterRequest): Promise<{ message: string; user: User; tenant: Tenant; tokens: AuthTokens }> {
    try {
      const response = await apiClient.post('/api/auth/register/', data);
      
      // Store tokens in Zustand
      if (response.data.tokens?.access || response.data.tokens?.refresh) {
        useAuthStore.getState().setTokens(response.data.tokens.access, response.data.tokens.refresh);
      }
      
      return response.data;
    } catch (error: any) {
      throw new Error(extractErrorMessage(error.response?.data || error));
    }
  }

  async logout(refreshToken: string): Promise<{ message: string }> {
    try {
      const response = await apiClient.post('/api/auth/logout/', { refresh_token: refreshToken });
      
      // Clear Zustand tokens
      useAuthStore.getState().clearAuth();
      
      return response.data;
    } catch (error: any) {
      // Clear tokens even if API call fails
      useAuthStore.getState().clearAuth();
      throw new Error(extractErrorMessage(error.response?.data || error));
    }
  }

  async refreshToken(refreshToken: string): Promise<{ access: string }> {
    try {
      const response = await apiClient.post('/api/auth/token/refresh/', { refresh: refreshToken });
      
      // Update Zustand token
      if (response.data.access) {
        useAuthStore.getState().setTokens(response.data.access, refreshToken);
      }
      
      return response.data;
    } catch (error: any) {
      throw new Error(extractErrorMessage(error.response?.data || error));
    }
  }

  async getUserInfo(): Promise<User> {
    try {
      const response = await apiClient.get('/api/auth/user/');
      return response.data.user;
    } catch (error: any) {
      throw new Error(extractErrorMessage(error.response?.data || error));
    }
  }

  async updateUserDetails(data: Partial<User>): Promise<User> {
    try {
      const response = await apiClient.put('/api/auth/user/details/', data);
      return response.data;
    } catch (error: any) {
      throw new Error(extractErrorMessage(error.response?.data || error));
    }
  }

  async updateUserProfile(data: Partial<UserProfile>): Promise<UserProfile> {
    try {
      const response = await apiClient.put('/api/auth/user/profile/', data);
      return response.data;
    } catch (error: any) {
      throw new Error(extractErrorMessage(error.response?.data || error));
    }
  }

  async changePassword(data: ChangePasswordRequest): Promise<{ message: string }> {
    try {
      const response = await apiClient.post('/api/auth/change-password/', data);
      return response.data;
    } catch (error: any) {
      throw new Error(extractErrorMessage(error.response?.data || error));
    }
  }

  async requestPasswordReset(data: PasswordResetRequest): Promise<{ message: string; token?: string }> {
    try {
      const response = await apiClient.post('/api/auth/password-reset/', data);
      return response.data;
    } catch (error: any) {
      throw new Error(extractErrorMessage(error.response?.data || error));
    }
  }

  async confirmPasswordReset(data: PasswordResetConfirmRequest): Promise<{ message: string }> {
    try {
      const response = await apiClient.post('/api/auth/password-reset/confirm/', data);
      return response.data;
    } catch (error: any) {
      throw new Error(extractErrorMessage(error.response?.data || error));
    }
  }

  async verifyEmail(data: EmailVerificationRequest): Promise<{ message: string }> {
    try {
      const response = await apiClient.post('/api/auth/verify-email/', data);
      return response.data;
    } catch (error: any) {
      throw new Error(extractErrorMessage(error.response?.data || error));
    }
  }

  async resendVerificationEmail(): Promise<{ message: string }> {
    try {
      const response = await apiClient.post('/api/auth/resend-verification/');
      return response.data;
    } catch (error: any) {
      throw new Error(extractErrorMessage(error.response?.data || error));
    }
  }

  // Add missing methods needed by AuthContext
  async getCurrentUser(): Promise<User> {
    return this.getUserInfo();
  }

  async signup(userData: RegisterRequest): Promise<{ message: string; user: User; tenant: Tenant; tokens: AuthTokens }> {
    return this.register(userData);
  }

  async forgotPassword(email: string): Promise<{ message: string; token?: string }> {
    return this.requestPasswordReset({ email });
  }

  async resetPassword(token: string, password: string): Promise<{ message: string }> {
    return this.confirmPasswordReset({
      token,
      new_password: password,
      new_password_confirm: password
    });
  }
}

// Create and export the service
export const authApiService = new AuthApiService();

// Export as authService to match imports in other files
export const authService = authApiService; 